import type { Policy, PtoBalance, PtoRequest, TimeOffEntry, TimeOffHistoryRow } from '../types/hr';

export const policies: Policy[] = [
  {
    id: 'newhire',
    name: 'New Hire PTO',
    tagline: 'First-year accrual with a 30-day waiting period and an anniversary top-up.',
    type: 'Accrual',
    updated: '2026-10-14',
    hasTbd: false,
    overview:
      'Applies to every new full-time hire during their first year. PTO begins accruing after a 30-day waiting period, is prorated by start date, and caps at 12 days. At the first work anniversary the balance is topped up to 15 days and the employee moves to Regular PTO.',
    highlights: [
      { label: 'Accrual', value: '1 day / month' },
      { label: 'Year-one cap', value: '12 days' },
      { label: 'Anniversary', value: 'Top up to 15' },
    ],
    sections: [
      {
        id: 'eligibility',
        title: 'Eligibility & waiting period',
        rules: [
          { label: 'Eligible employees', value: 'All new full-time hires, from hire date to first work anniversary' },
          { label: 'Waiting period', value: '30 calendar days from hire date' },
          { label: 'Accrual begins', value: 'Day 31 of employment' },
          { label: 'During waiting period', value: 'Requests allowed; unpaid unless approved as an advance' },
        ],
      },
      {
        id: 'accrual',
        title: 'Accrual',
        rules: [
          { label: 'Accrual rate', value: '1 day per month' },
          { label: 'Frequency', value: 'Monthly, posted on the 1st' },
          { label: 'Proration', value: 'Yes — first partial month prorated by days employed after waiting period' },
          { label: 'Accrual pauses', value: 'When the balance reaches 12 days' },
        ],
      },
      {
        id: 'caps',
        title: 'Caps',
        rules: [
          { label: 'Year-one maximum', value: '12 days' },
          { label: 'Negative balance', value: 'Not allowed' },
        ],
      },
      {
        id: 'anniversary',
        title: 'Anniversary rule',
        rules: [
          { label: 'Trigger', value: 'First work anniversary' },
          { label: 'Rule', value: 'Top balance UP TO 15 days (not +15)', note: 'If the balance is already 15 or more, nothing is added.' },
          { label: 'After anniversary', value: 'Employee transitions to Regular PTO' },
        ],
      },
      {
        id: 'carryover',
        title: 'Carryover',
        rules: [
          { label: 'Carryover', value: 'Full balance carries into Regular PTO at the anniversary' },
          { label: 'Year-end', value: 'No forfeiture during year one' },
        ],
      },
    ],
    example: {
      before: '10 days banked',
      added: '+5 days',
      after: '15 days',
      caption:
        'The top-up brings the balance to 15 — it never adds a flat 15. An employee with 14 days receives +1; an employee with 15 or more receives nothing.',
    },
    form: {
      name: 'New Hire PTO',
      type: 'Accrual',
      accrualRate: '1',
      accrualFrequency: 'Monthly',
      waitingDays: '30',
      prorate: true,
      yearOneCap: '12',
      anniversaryMode: 'top-up',
      anniversaryAmount: '15',
      carryover: '15',
      departments: ['Operations', 'Underwriting', 'Processing', 'Closing', 'Sales', 'Marketing', 'IT', 'Finance', 'HR'],
    },
  },
  {
    id: 'regular',
    name: 'Regular PTO',
    tagline: 'Ongoing PTO for operations staff after their first anniversary. Several values pending.',
    type: 'Accrual',
    updated: '2026-10-28',
    hasTbd: true,
    overview:
      'Ongoing paid time off for operations staff who have completed their first year. Accrual, cap and carryover values are placeholders while leadership finalizes the policy — they are clearly marked TBD below.',
    highlights: [
      { label: 'Accrual', value: 'TBD', tbd: true },
      { label: 'Cap', value: 'TBD', tbd: true },
      { label: 'Carryover', value: 'TBD', tbd: true },
    ],
    sections: [
      {
        id: 'eligibility',
        title: 'Eligibility & waiting period',
        rules: [
          { label: 'Eligible employees', value: 'Operations staff past their first work anniversary' },
          { label: 'Waiting period', value: 'None — transfers in from New Hire PTO' },
        ],
      },
      {
        id: 'accrual',
        title: 'Accrual',
        rules: [
          { label: 'Accrual rate', value: 'TBD', tbd: true },
          { label: 'Frequency', value: 'TBD', tbd: true },
          { label: 'Proration', value: 'TBD', tbd: true },
        ],
      },
      {
        id: 'caps',
        title: 'Caps',
        rules: [
          { label: 'Annual cap', value: 'TBD', tbd: true },
          { label: 'Maximum balance', value: 'TBD', tbd: true },
        ],
      },
      {
        id: 'anniversary',
        title: 'Anniversary rule',
        rules: [{ label: 'Anniversary adjustment', value: 'TBD', tbd: true }],
      },
      {
        id: 'carryover',
        title: 'Carryover',
        rules: [
          { label: 'Carryover limit', value: 'TBD', tbd: true },
          { label: 'Year-end treatment', value: 'TBD', tbd: true },
        ],
      },
    ],
    form: {
      name: 'Regular PTO',
      type: 'Accrual',
      accrualRate: '',
      accrualFrequency: 'Monthly',
      waitingDays: '0',
      prorate: false,
      yearOneCap: '',
      anniversaryMode: 'add',
      anniversaryAmount: '',
      carryover: '',
      departments: ['Operations'],
    },
  },
];

// Approved time off. Regular PTO entries belong to the Operations pilot group.
export const timeOffEntries: TimeOffEntry[] = [
  { id: 't1', employeeId: 'e25', start: '2026-11-03', end: '2026-11-05', days: 3, policyLabel: 'Regular PTO', tone: 'blue' },
  { id: 't2', employeeId: 'e26', start: '2026-11-04', end: '2026-11-04', days: 1, policyLabel: 'Regular PTO', tone: 'blue' },
  { id: 't3', employeeId: 'e27', start: '2026-11-04', end: '2026-11-06', days: 3, policyLabel: 'Regular PTO', tone: 'blue' },
  { id: 't4', employeeId: 'e28', start: '2026-11-02', end: '2026-11-06', days: 5, policyLabel: 'Regular PTO', tone: 'blue' },
  { id: 't5', employeeId: 'e29', start: '2026-11-04', end: '2026-11-04', days: 0.5, policyLabel: 'Regular PTO', tone: 'blue' },
  { id: 't6', employeeId: 'e21', start: '2026-10-01', end: '2026-12-31', days: 66, policyLabel: 'Leave of Absence', tone: 'teal' },
  { id: 't7', employeeId: 'e30', start: '2026-11-06', end: '2026-11-06', days: 1, policyLabel: 'Regular PTO', tone: 'blue' },
  { id: 't8', employeeId: 'e31', start: '2026-11-05', end: '2026-11-06', days: 2, policyLabel: 'Regular PTO', tone: 'blue' },
  { id: 't9', employeeId: 'e32', start: '2026-11-12', end: '2026-11-13', days: 2, policyLabel: 'Regular PTO', tone: 'blue' },
  { id: 't10', employeeId: 'e33', start: '2026-11-16', end: '2026-11-20', days: 5, policyLabel: 'Regular PTO', tone: 'blue' },
  { id: 't11', employeeId: 'e34', start: '2026-11-18', end: '2026-11-19', days: 2, policyLabel: 'Regular PTO', tone: 'blue' },
  { id: 't12', employeeId: 'e35', start: '2026-11-23', end: '2026-11-24', days: 2, policyLabel: 'Regular PTO', tone: 'blue' },
  { id: 't13', employeeId: 'e36', start: '2026-11-25', end: '2026-11-27', days: 3, policyLabel: 'Regular PTO', tone: 'blue' },
  { id: 't14', employeeId: 'e37', start: '2026-12-14', end: '2026-12-18', days: 5, policyLabel: 'Regular PTO', tone: 'blue' },
];

export const ptoRequests: PtoRequest[] = [
  { id: 'r1', employeeId: 'e38', start: '2026-11-24', end: '2026-11-26', days: 2.5, policyId: 'regular', note: 'Thanksgiving travel — back online Monday.', submitted: '2026-11-01', balanceBefore: 8, status: 'pending' },
  { id: 'r2', employeeId: 'e39', start: '2026-12-21', end: '2026-12-31', days: 7, policyId: 'regular', note: 'Holiday trip to visit family.', submitted: '2026-11-02', balanceBefore: 11.5, status: 'pending' },
  { id: 'r3', employeeId: 'e31', start: '2026-11-20', end: '2026-11-20', days: 1, policyId: 'regular', note: 'Medical appointment.', submitted: '2026-11-03', balanceBefore: 8, status: 'pending' },
  { id: 'r4', employeeId: 'e33', start: '2026-12-07', end: '2026-12-08', days: 2, policyId: 'regular', note: 'Moving apartments.', submitted: '2026-11-03', balanceBefore: 9.5, status: 'pending' },
  { id: 'r5', employeeId: 'e8', start: '2026-12-24', end: '2026-12-24', days: 1, policyId: 'newhire', note: 'Holiday — I understand I may still be in my waiting period.', submitted: '2026-11-04', balanceBefore: 0, status: 'pending' },
];

export const balances: PtoBalance[] = [
  { employeeId: 'e25', policyId: 'regular', available: 9.5 },
  { employeeId: 'e26', policyId: 'regular', available: 8 },
  { employeeId: 'e27', policyId: 'regular', available: 10 },
  { employeeId: 'e28', policyId: 'regular', available: 7.5 },
  { employeeId: 'e29', policyId: 'regular', available: 8.5 },
  { employeeId: 'e30', policyId: 'regular', available: 11 },
  { employeeId: 'e31', policyId: 'regular', available: 8 },
  { employeeId: 'e32', policyId: 'regular', available: 9 },
  { employeeId: 'e33', policyId: 'regular', available: 9.5 },
  { employeeId: 'e34', policyId: 'regular', available: 12 },
  { employeeId: 'e35', policyId: 'regular', available: 7.5 },
  { employeeId: 'e36', policyId: 'regular', available: 10.5 },
  { employeeId: 'e37', policyId: 'regular', available: 9 },
  { employeeId: 'e38', policyId: 'regular', available: 8 },
  { employeeId: 'e39', policyId: 'regular', available: 11.5 },
  { employeeId: 'e8', policyId: 'newhire', available: 0 },
  { employeeId: 'e13', policyId: 'newhire', available: 0 },
  { employeeId: 'e16', policyId: 'newhire', available: 0 },
  { employeeId: 'e23', policyId: 'newhire', available: 0 },
];

/**
 * Regular PTO ledger template. `balance` here is an OFFSET from the employee's
 * current available balance. Accrual rows carry no amount because the rate is TBD.
 */
export const regularHistoryTemplate: TimeOffHistoryRow[] = [
  { id: 'h1', date: '2026-10-31', description: 'Monthly accrual (October) — rate TBD', pendingAccrual: true, balance: 0, year: 2026 },
  { id: 'h2', date: '2026-10-12', description: 'Time off — Columbus Day bridge', used: 1, balance: 0, year: 2026 },
  { id: 'h3', date: '2026-09-30', description: 'Monthly accrual (September) — rate TBD', pendingAccrual: true, balance: 1, year: 2026 },
  { id: 'h4', date: '2026-07-06', description: 'Time off — Summer vacation (Jul 6–8)', used: 3, balance: 1, year: 2026 },
  { id: 'h5', date: '2026-05-22', description: 'Time off — Personal day', used: 1, balance: 4, year: 2026 },
  { id: 'h6', date: '2026-02-16', description: "Time off — Presidents' Day bridge", used: 1, balance: 5, year: 2026 },
  { id: 'h7', date: '2026-01-01', description: 'Balance carried forward from 2025', balance: 6, year: 2026 },
  { id: 'h8', date: '2025-12-22', description: 'Time off — Holiday (Dec 22–24)', used: 3, balance: 6, year: 2025 },
  { id: 'h9', date: '2025-11-26', description: 'Time off — Thanksgiving', used: 1, balance: 9, year: 2025 },
];
